import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth, hasRole } from "../lib/auth";
import { PageHeader, Section } from "../components/Primitives";
import { applyBrandingVars, useBranding } from "../lib/branding";
import { toast } from "sonner";

export default function Branding() {
    const { user } = useAuth();
    const { tenant, setTenant } = useBranding();
    const [agencies, setAgencies] = useState([]);
    const [agencyId, setAgencyId] = useState(user.role === "admin" ? user.agency_id : "");
    const [agencyForm, setAgencyForm] = useState(null);

    const [advertisers, setAdvertisers] = useState([]);
    const [advertiserId, setAdvertiserId] = useState("");
    const [advForm, setAdvForm] = useState(null);

    useEffect(() => {
        if (user.role === "super_admin") {
            api.get("/agencies").then((r) => {
                setAgencies(r.data);
                if (!agencyId && r.data.length) setAgencyId(r.data[0].id);
            });
        }
        if (hasRole(user, "super_admin", "admin")) {
            api.get("/advertisers").then((r) => setAdvertisers(r.data));
        }
    }, [user.role]); // eslint-disable-line

    useEffect(() => {
        if (!agencyId) return;
        api.get(`/agencies/${agencyId}/branding`).then((r) => setAgencyForm(r.data));
    }, [agencyId]);

    useEffect(() => {
        if (!advertiserId) {
            setAdvForm(null);
            return;
        }
        api.get(`/advertisers/${advertiserId}/branding`).then((r) => setAdvForm(r.data));
    }, [advertiserId]);

    const saveAgency = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.put(`/agencies/${agencyId}/branding`, agencyForm);
            setAgencyForm(data);
            applyBrandingVars(data.primary_color, data.secondary_color);
            setTenant({ ...tenant, branding: data });
            toast.success("Agency branding saved — colors applied live");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const saveAdv = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.put(`/advertisers/${advertiserId}/branding`, advForm);
            setAdvForm(data);
            toast.success("Advertiser report branding saved");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    return (
        <div data-testid="branding-page">
            <PageHeader eyebrow="White-label" title="Branding" subtitle="Agency branding controls the dashboard shell. Advertiser branding is applied to report exports." />

            <Section title="Agency dashboard branding" testId="section-agency-branding">
                {user.role === "super_admin" && (
                    <div className="mb-3">
                        <label className="overline">Agency</label>
                        <select value={agencyId} onChange={(e) => setAgencyId(e.target.value)} className="ml-2 px-3 py-1.5 bg-zinc-100 border border-zinc-200 rounded text-sm">
                            {agencies.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                        </select>
                    </div>
                )}
                {agencyForm && (
                    <form onSubmit={saveAgency} className="bg-white border border-zinc-200 p-5 grid md:grid-cols-2 gap-4" data-testid="agency-branding-form">
                        <div>
                            <label className="overline">Brand name</label>
                            <input value={agencyForm.brand_name || ""} onChange={(e) => setAgencyForm({ ...agencyForm, brand_name: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="brand-name-input" />
                        </div>
                        <div>
                            <label className="overline">Logo URL</label>
                            <input value={agencyForm.logo_url || ""} onChange={(e) => setAgencyForm({ ...agencyForm, logo_url: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" />
                        </div>
                        <div>
                            <label className="overline">Primary color</label>
                            <div className="mt-1 flex gap-2 items-center">
                                <input type="color" value={agencyForm.primary_color || "#f97316"} onChange={(e) => setAgencyForm({ ...agencyForm, primary_color: e.target.value })} className="w-12 h-10 bg-transparent border border-zinc-200 rounded" data-testid="primary-color-input" />
                                <input value={agencyForm.primary_color || ""} onChange={(e) => setAgencyForm({ ...agencyForm, primary_color: e.target.value })} className="flex-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded font-mono text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="overline">Secondary color</label>
                            <div className="mt-1 flex gap-2 items-center">
                                <input type="color" value={agencyForm.secondary_color || "#22d3ee"} onChange={(e) => setAgencyForm({ ...agencyForm, secondary_color: e.target.value })} className="w-12 h-10 bg-transparent border border-zinc-200 rounded" />
                                <input value={agencyForm.secondary_color || ""} onChange={(e) => setAgencyForm({ ...agencyForm, secondary_color: e.target.value })} className="flex-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded font-mono text-sm" />
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <label className="overline">Login tagline</label>
                            <input value={agencyForm.login_tagline || ""} onChange={(e) => setAgencyForm({ ...agencyForm, login_tagline: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" />
                        </div>
                        <div>
                            <label className="overline">Export header</label>
                            <input value={agencyForm.export_header_text || ""} onChange={(e) => setAgencyForm({ ...agencyForm, export_header_text: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" />
                        </div>
                        <div>
                            <label className="overline">Export footer</label>
                            <input value={agencyForm.export_footer_text || ""} onChange={(e) => setAgencyForm({ ...agencyForm, export_footer_text: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" />
                        </div>
                        <div className="md:col-span-2 flex justify-end">
                            <button type="submit" className="px-4 py-2 rounded text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="agency-branding-save">Save & apply</button>
                        </div>
                    </form>
                )}
            </Section>

            <Section title="Advertiser report branding" testId="section-advertiser-branding">
                <div className="mb-3">
                    <label className="overline">Advertiser</label>
                    <select value={advertiserId} onChange={(e) => setAdvertiserId(e.target.value)} className="ml-2 px-3 py-1.5 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="advertiser-branding-select">
                        <option value="">— pick an advertiser —</option>
                        {advertisers.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                    </select>
                </div>
                {advForm && (
                    <form onSubmit={saveAdv} className="bg-white border border-zinc-200 p-5 grid md:grid-cols-2 gap-4" data-testid="advertiser-branding-form">
                        <div>
                            <label className="overline">Display name</label>
                            <input value={advForm.display_name || ""} onChange={(e) => setAdvForm({ ...advForm, display_name: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" />
                        </div>
                        <div>
                            <label className="overline">Logo URL</label>
                            <input value={advForm.logo_url || ""} onChange={(e) => setAdvForm({ ...advForm, logo_url: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" />
                        </div>
                        <div>
                            <label className="overline">Export primary color</label>
                            <div className="mt-1 flex gap-2 items-center">
                                <input type="color" value={advForm.export_primary_color || "#6366f1"} onChange={(e) => setAdvForm({ ...advForm, export_primary_color: e.target.value })} className="w-12 h-10 bg-transparent border border-zinc-200 rounded" />
                                <input value={advForm.export_primary_color || ""} onChange={(e) => setAdvForm({ ...advForm, export_primary_color: e.target.value })} className="flex-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded font-mono text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="overline">Report header</label>
                            <input value={advForm.report_header || ""} onChange={(e) => setAdvForm({ ...advForm, report_header: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="overline">Report footer / contact block</label>
                            <textarea rows={3} value={advForm.report_footer || ""} onChange={(e) => setAdvForm({ ...advForm, report_footer: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" />
                        </div>
                        <div className="md:col-span-2 flex justify-end">
                            <button type="submit" className="px-4 py-2 rounded text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="advertiser-branding-save">Save</button>
                        </div>
                    </form>
                )}
            </Section>
        </div>
    );
}
